import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { tours as baseTours } from '../data/tours';
import { rentalVehicleOptions as baseRentalVehicleOptions } from '../data/rentalVehicleOptions';
import { apiUrl } from '../utils/api';

const defaultPricingState = {
  tours: baseTours,
  rentalVehicleOptions: baseRentalVehicleOptions,
  refreshPricing: async () => {},
};

const PricingContext = createContext(defaultPricingState);

function buildRentalPriceLabel(option) {
  if (option.quoteOnly) {
    return option.priceLabel || 'Quote required';
  }

  if (typeof option.price === 'number') {
    return `From $${option.price} / day`;
  }

  return option.priceLabel || '';
}

function applyPricingOverrides(overrides = {}) {
  const tourPrices = overrides.tourPrices || {};
  const rentalVehiclePrices = overrides.rentalVehiclePrices || {};

  return {
    tours: baseTours.map((tour) => ({
      ...tour,
      ...(tourPrices[tour.id] || {}),
    })),
    rentalVehicleOptions: baseRentalVehicleOptions.map((option) => {
      const mergedOption = {
        ...option,
        ...(rentalVehiclePrices[option.key] || {}),
      };

      return {
        ...mergedOption,
        priceLabel: buildRentalPriceLabel(mergedOption),
      };
    }),
  };
}

export function PricingProvider({ children }) {
  const [overrides, setOverrides] = useState({ tourPrices: {}, rentalVehiclePrices: {} });

  const refreshPricing = async () => {
    try {
      const response = await fetch(apiUrl('/api/pricing-overrides'));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Could not load pricing.');
      }

      setOverrides({
        tourPrices: data.tourPrices || {},
        rentalVehiclePrices: data.rentalVehiclePrices || {},
      });
    } catch (error) {
      console.error('Could not load pricing overrides:', error);
    }
  };

  useEffect(() => {
    refreshPricing();
  }, []);

  const value = useMemo(() => {
    const mergedPricing = applyPricingOverrides(overrides);

    return {
      ...mergedPricing,
      refreshPricing,
    };
  }, [overrides]);

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
}

export function usePricing() {
  return useContext(PricingContext);
}
